import { ISection } from '../../../interfaces/scheduler_interfaces';
import { IQuery } from 'core-ts-types';
import { Query } from 'utility-ts-orm';
import * as _4 from 'lodash4';
import { SectionSelectionsModel } from '../models/section_selections_model';
import { ISectionsDatasource } from '../../../views/sections/datasources/sections_datasource';
import { ScheduledSectionServant } from '../../../views/course_scheduler/servants/scheduled_section_servant';
import { Section } from '../../../views/sections/builders/section_builder';
import { RegisteredSectionServant } from '../../../views/course_scheduler/servants/registered_section_servant';

type TSectionsSet = Set<Section['id']>;

export class SectionSelectionsQuery implements IQuery {
  private registerSelections: ISection[] = [];
  private waitlistSelections: ISection[] = [];
  private withdrawSelections: ISection[] = [];
  private dropSelections: ISection[] = [];

  private _result: {
    activeSelectionSet: Set<number>;
    registerSelections: ISection[];
    dropSelections: ISection[];
    withdrawSelections: ISection[];
    waitlistSelections: ISection[];
  };

  constructor(
    private ngSectionSelectionsModel: SectionSelectionsModel,
    private ngSectionsDatasource: ISectionsDatasource,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngRegisteredSectionServant: RegisteredSectionServant
  ) {
    Query.registerQuery(this, ngSectionSelectionsModel);
  }

  private getSelectedSectionsId(sectionSet: TSectionsSet) {
    const selectedSectionsInfo = this.getSelectedSectionsInfo(sectionSet);
    const selectedSectionsId = this.getSectionsId(selectedSectionsInfo);
    return selectedSectionsId;
  }

  private getSelectedSectionsInfo(sectionSet: TSectionsSet) {
    const allSections = this.ngSectionsDatasource.read();
    return _4.filter(allSections, (section) => sectionSet.has(section.id));
  }

  private getSectionsId(sections: ISection[]) {
    return _4.map(sections, 'id') as ISection['id'][];
  }

  private clearAllSelection() {
    this.registerSelections = [];
    this.waitlistSelections = [];
    this.withdrawSelections = [];
    this.dropSelections = [];
  }

  /**
   * Waitlisted sections can be registered and/or dropped
   * @param section
   */
  private handleWaitlistedSection(section: Section): void {
    const canSectionBeRegistered = this.ngScheduledSectionServant.canBeRegistered(section);
    const canSectionBeDropped = this.ngRegisteredSectionServant.canBeDropped(section);
    if (canSectionBeRegistered) {
      this.registerSelections.push(section);
    }
    if (canSectionBeDropped) {
      this.dropSelections.push(section);
    }
  }

  /**
   * Scheduled sections can either be registered or waitlisted
   * @param section
   */
  private handleScheduledSection(section: Section): void {
    const canSectionBeRegistered = this.ngScheduledSectionServant.canBeRegistered(section);
    const canSectionBeWaitlisted = this.ngScheduledSectionServant.canBeWaitlisted(section);

    if (canSectionBeWaitlisted) {
      this.waitlistSelections.push(section);
      return;
    }
    if (canSectionBeRegistered) {
      this.registerSelections.push(section);
    }
  }

  /**
   * Registered section can either be dropped or withdrawn
   * @param section
   */
  private handleRegisteredSection(section: Section): void {
    const canSectionBeWithdrawn = this.ngRegisteredSectionServant.canBeWithdrawn(section);
    const canSectionBeDropped = this.ngRegisteredSectionServant.canBeDropped(section);

    if (canSectionBeWithdrawn) {
      this.withdrawSelections.push(section);
      return;
    }
    if (canSectionBeDropped) {
      this.dropSelections.push(section);
    }
  }

  public processSections(selectedSectionIds: number[]): void {
    for (const selectedSectionId of selectedSectionIds) {
      const section = this.ngSectionsDatasource.get(selectedSectionId);
      if (!section) continue;
      const isScheduledSection = this.ngScheduledSectionServant.isSectionScheduled_(section);
      const isWaitlistedSection = ScheduledSectionServant.isSectionWaitListed(section);
      const isRegisteredSection = this.ngScheduledSectionServant.isSectionRegistered(section);

      if (isWaitlistedSection) {
        this.handleWaitlistedSection(section);
        continue;
      }

      if (isScheduledSection) {
        this.handleScheduledSection(section);
        continue;
      }

      if (isRegisteredSection) {
        this.handleRegisteredSection(section);
      }
    }
  }

  run() {
    const selectedSectionIds = Array.from(this.ngSectionSelectionsModel.read().sectionIds);

    this.clearAllSelection();
    this.processSections(selectedSectionIds);

    this._result = {
      activeSelectionSet: new Set(selectedSectionIds),
      registerSelections: this.registerSelections,
      dropSelections: this.dropSelections,
      withdrawSelections: this.withdrawSelections,
      waitlistSelections: this.waitlistSelections
    };

    return this;
  }
  get result() {
    return this._result;
  }
}

angular.module('capability-ng-scheduler').service('ngSectionSelectionsQuery', SectionSelectionsQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/components/section_selections/queries/section_selections_query.ts